import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useLayoutEffect } from 'react';
import { 
  Divider, Flex, Dropdown, Popover, Button, Space, Modal, App, Typography, Table,
} from 'antd';
import { 
  VerticalRightOutlined, VerticalLeftOutlined, 
  DownOutlined, LoadingOutlined,
} from '@ant-design/icons';
import _ from 'lodash';

import md5 from 'blueimp-md5';

// 引入 Ant Design X 组件
import { 
  Bubble, Sender, ThoughtChain, XProvider, Conversations,
} from '@ant-design/x';
import type { BubbleProps, } from '@ant-design/x';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { WaenMarked } from 'react-waenui-mdeditor';
import isMobile from 'ismobilejs';
import './ai.css';


// ================= 类型定义 =================
interface Message {
  key: string;
  placement?: 'start' | 'end';
  content?: string;
  // avatar?: string;
  loading?: boolean;
  loadingRender?: any;
  messageRender?: any;
}

interface AppProps {}

const { useApp } = App;

const renderMarkdown: BubbleProps['messageRender'] = (content) => {
  return (
    <Typography>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: used in demo */}
      <div dangerouslySetInnerHTML={{ __html: WaenMarked(content) }} />
    </Typography>
  );
};

const loadMessages = (userId: string | null, qid: string | null, qtype: string | null): Message[] => {
  if (!qid) return [];
  const messages = localStorage.getItem(`bublelist${userId}${qtype}${qid}`);
  const msgs = messages ? JSON.parse(messages) : [];
  return msgs;
};

const getAskItems = (qtype:string) => {
  return qtype === 'question' ?
  [{key: '1', label: '题面理解关键信息'}, {key: '2', label: '解题思路分析'}, {key: '3', label: '主要考查的知识点'}, {key: '4', label: '解题步骤分解'}, {key: '100', label: '我的代码分析'}, {key: '101', label: '官方答案'}, {key: '102', label: '官方答案代码详细解读'}, {key: '103', label: '官方解析'}] :
  [{key: '1', label: '题面理解关键信息'}, {key: '2', label: '解题思路分析'}, {key: '3', label: '主要考查的知识点'}, {key: '101', label: '官方答案'},  {key: '103', label: '官方解析'}];
}

// ================= 组件定义 =================

/**
 * 左侧边栏组件
 */
const Sidebar: React.FC<{
  showLeftBar: boolean;
  setShowLeftBar: (show: boolean) => void;
  qtype: string | null,
  selAskId: string | null;
  userInfo: any;
  toLogin: any;
  toLogout: any;
  selectAsk: any;
}> = ({ showLeftBar, setShowLeftBar, qtype, selAskId, userInfo, toLogin, toLogout, selectAsk }) => {
  if (!showLeftBar) return null;

  return (
    <>
      <Flex vertical style={{ width: 200, height: '100%' }}>
        <Flex justify="space-between" align="center" style={{ padding: '0 12px' }}>
          <span style={{ fontSize: 18 }}>华恩智教</span>
          <Popover content="收起侧栏">
            <Button 
              onClick={() => setShowLeftBar(false)}
              icon={<VerticalRightOutlined />}
            />
          </Popover>
        </Flex>
        <Conversations
          style={{ width: 200, flex: 1, overflowY: 'auto' }}
          activeKey={selAskId || ''}
          items={getAskItems(qtype || '')}
          onActiveChange={selectAsk}
        />
        <div>
          {
            userInfo.userId ? <Dropdown menu={{items:[{
              label: '首页',
              key: '1',
            },
            {
              label: '登出',
              key: '0',
            }],
            onClick: (menuInfo) => {
              menuInfo.domEvent.stopPropagation();
              if (menuInfo.key === '1') {
                window.location.href = '/'
              } else if (menuInfo.key === '0') {
                toLogout();
              }
            }
          }}
            placement="top"
            >
              <a type="text" onClick={(e) => e.preventDefault()}>
                <Space style={{ width: '200px', height: '40px' }}>
                  <div style={{ width: 156, textAlign: 'right' }}>{userInfo.userName || userInfo.userId}</div>
                  <div style={{ width: '20px', height: '20px' }}><DownOutlined /></div>
                </Space>
              </a>
          </Dropdown> :
          <Button type="text" block onClick={toLogin}>
            未登录
          </Button>
          }
        </div>
      </Flex>
      <Divider type="vertical" style={{ height: '100%' }} />
    </>
  );
};

/**
 * 聊天区域头部组件
 */
const ChatHeader: React.FC<{
  showLeftBar: boolean;
  setShowLeftBar: (show: boolean) => void;
  qid: string | null;
  qname: string | null;
}> = ({ showLeftBar, setShowLeftBar, qid, qname }) => {
  return (
    <Flex align="center" style={{ padding: '0 10px' }}>
      {!showLeftBar && (
        <div style={{ marginRight: 20 }}>
          <Popover content="展开侧栏">
            <Button 
              onClick={() => setShowLeftBar(true)}
              icon={<VerticalLeftOutlined />}
              style={{ marginRight: 5 }}
            />
          </Popover>
        </div>
      )}
      <Space>{(qname || '').length > 64 ? `${qname?.substring(0, 64)}...` : qname}</Space>
    </Flex>
  );
};

export interface ScrollDotHandle {
  scrollTo: () => void;
}

/**
 * 消息列表组件
 */
const MessageList = forwardRef<ScrollDotHandle, {
  messages: Message[];
  usermsg: string;
  aimsg: string;
  messageSending: string;
  shouldScroll: boolean;
  completeTyping: any;
  reAsk: any;
}>(({ messages, usermsg, aimsg, messageSending, shouldScroll, completeTyping, reAsk }, ref) => {
  // 创建子组件内部ref
  const scrolldotRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState<any>(0);

  useEffect(() => {
    if (messageSending === 'sending') {
      setTimeout(() => {
        setCount(count + 1);
        scrolldotRef.current?.scrollIntoView({ behavior: "smooth", block: "end"  });
      }, 500);
    } else {
      setCount(0);
    }
  }, [count, messageSending])
  useLayoutEffect(() => {
      scrolldotRef.current?.scrollIntoView({ behavior: "smooth", block: "end"  });
  }, [shouldScroll, messages, aimsg, messageSending]);

  useImperativeHandle(ref, () => ({
    scrollTo: () => {
      scrolldotRef.current?.scrollIntoView({ behavior: 'smooth', block: "end" });
    }
  }));

  if (messages.length === 0 && messageSending === 'stop') {
    return (
      <div style={{ flex: 1, overflowY: 'auto', textAlign: 'center', padding: 20 }} className="message-list">
        欢迎您！我是您的解题助手。您可以选择左侧的预设问题，也可以自己输入问题与您会话!
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflowY: 'auto' }} className="message-list">
      {
        messages.map(msg => {
          return msg.placement === 'end' ? <Bubble key={msg.key} style={{ margin: 10 }} placement={msg.placement} content={msg.content || ''} /> : <Bubble key={msg.key} style={{ width: '90%' }} placement={msg.placement} messageRender={renderMarkdown} content={msg.content || ''} />
      })
      }
      {
        messageSending !== 'stop' ? 
        <div style={{ minHeight: '100%' }}>
          <Bubble key="user" style={{ margin: 10 }} placement="end" content={usermsg || ''} />
          <Bubble
            key="ai"
            messageRender={renderMarkdown} content={aimsg || '正在思考中...'}
            typing={messageSending !== 'interrupt'}
            style={{ width: '90%' }}
            placement="start"
            onTypingComplete={completeTyping}
            loading={messageSending === 'thinking'}
          />
          <div style={{ textAlign: 'center', display: messageSending === 'interrupt' ? 'block' : 'none' }}>
            <span>你停止生成了本次回答</span>
            <span style={{ color: 'blue', cursor: 'pointer', marginLeft: 10 }} onClick={reAsk}>重新编辑问题</span>
          </div>
        </div> 
        : null
      }
      <div ref={scrolldotRef} id="scrolldot" />
    </div>
  );
});

/**
 * 主应用组件
 */
const AiQuestionChatApp: React.FC<AppProps> = () => {
  // ================= 状态管理 =================
  const isWWW = window.location.host.indexOf('www.itbegin.com') === 0 || window.location.host.indexOf('www2.itbegin.com') === 0 || window.location.host.indexOf('www1.itbegin.com') === 0
  const apiHost = !isWWW && window.location.port ? 'http://www2.itbegin.com:9090' : 'https://www.itbegin.com';
  const { qid: paramqid, qtype: paramqtype } = useParams();
  const [searchParams] = useSearchParams();
  const [qid, setQid] = useState<string | null>(paramqid || '');
  const [qtype, setQtype] = useState<string | null>(paramqtype || '');
  const [selAskId, setSelAskId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [showLeftBar, setShowLeftBar] = useState<boolean>(true);
  const [messageSending, setMessageSending] = useState<string>('stop');
  const [inputValue, setInputValue] = useState<string>('');
  const [usermsg, setUsermsg] = useState<string>('');
  const [aimsg, setAimsg] = useState<string>('');
  const [shouldScroll, setShouldScroll] = useState(false);
  const [rtmsg, setRtmsg] = useState<any>(null);
  const [duration, setDuration] = useState<number>(-1);
  const [userInfo, setUserInfo] = useState<any>({});
  const [isModalLogin, setIsModalLogin] = useState<boolean>(false);
  const [isModalService, setIsModalService] = useState<boolean>(false);
  const [wxPayParams, setWxPayParams] = useState<any>({nonce_str: '', prepay_id: '', appid: ''})
  const [curCq, setCurCq] = useState<any>({});
  const moocId = searchParams.get('moocId');
  const { message } = useApp();
  // const abortController = useRef<AbortController>(null);
  const scrollRef = useRef<ScrollDotHandle>(null);
  const senderRef = useRef<any>(null);
  const stateRef = useRef({
      qid: null as string | null,
      qtype: null as string | null,
      messageSending: 'stop',
      rtmsg: null,
      usermsg: null as string | null,
      aimsg: null as string | null
    });

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    if (paramqid && paramqtype) {
        setQid(paramqid);
        setQtype(paramqtype)
        localStorage.setItem('qid', paramqid);
        localStorage.setItem('qtype', paramqtype);
        if(userInfo && userInfo.userId) {
          setMessages(loadMessages(userInfo.userId || '', paramqid || '', paramqtype || ''));
        } else {
          setMessages([]);    
        }
    } else {
      setMessages([]);
    }
    setShouldScroll(true);
    getLoginInfo();
    towxrecharge();
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, []);
  
  useEffect(() => {
    stateRef.current = { 
      qid,
      qtype,
      messageSending ,
      rtmsg,
      usermsg,
      aimsg
    };
  }, [qid, qtype, messageSending, rtmsg, usermsg, aimsg]);

  useEffect(() => {
    if (userInfo && userInfo.userId) {
      setMessages(loadMessages(userInfo.userId || '', qid, qtype));
    } else {
      setMessages([]);
    }
  }, [userInfo]);

  useEffect(() => {
    if (wxPayParams.nonce_str && wxPayParams.prepay_id && wxPayParams.appid) {
      wxpay();
    }
  }, [wxPayParams]);

  const handleBeforeUnload = () => {
    const { qid, qtype, messageSending, rtmsg, usermsg, aimsg } = stateRef.current;
    if (qid && qtype && messageSending !== 'stop' && rtmsg) {
      const _rtmsg = rtmsg ? rtmsg : {id: `${Date.now()}`, askmessage: usermsg, answermessage: aimsg, qid };
      const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: _rtmsg.askmessage || '', placement: 'end' };
      const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: (messageSending === 'interrupt' ? aimsg : _rtmsg.answermessage) || '', placement: 'start' };
      const _messages = loadMessages(userInfo.userId, qid, qtype);
      _messages.push(_usermsg);
      _messages.push(_replymsg);
      localStorage.setItem(`bublelist${userInfo.userId}${qtype}${qid}`, JSON.stringify(_messages));
    }
  };

  const getLoginInfo = () => {
    const getSelf = isWWW ? axios.get('/api/system/self') : axios.get(`${apiHost}/api/system/self`, { withCredentials: true });
    getSelf.then((res) => {
      const userInfo = res && res.data && res.data.id
        ? {
          userId: res.data.id,
          userName: res.data.name,
          schoolCode: res.data.schoolUser ? res.data.schoolUser.schoolCode : '',
          role: res.data.schoolUser ? res.data.schoolUser.role : '',
          phoneNum: res.data.phoneNum ? res.data.phoneNum : '',
        }
        : {}
        setUserInfo(userInfo);
        localStorage.setItem('userId', userInfo.userId);
      if (userInfo.userId) {
        const uri = qtype === 'question' ? `/api/codequestions/get/${qid}` : `/api/commonquestions/${qid}`;
        const getQuestion = isWWW ? axios.get(uri) : axios.get(`${apiHost}${uri}`, { withCredentials: true });
        getQuestion.then((res) => {
          if (qtype === 'question') {
            if (res.data.success) {
              setCurCq(res.data.data);
            }
          } else if (res.status === 200) {
            setCurCq(res.data);
          }
        })
      } else {
        message.error('你还没有登录!');
      }
    }).catch((error) => {
      console.log('error=', error);
      setUserInfo({});
      localStorage.setItem('userId', '');
    })
  }

  const completeTyping = () => {
    if (messageSending === 'interrupt' || !rtmsg || _.findIndex(messages, {key: `${rtmsg.id}_1`}) >= 0) {
      return;
    }
    saveMsg();
  }

  const saveMsg = () => {
    const _rtmsg = rtmsg ? rtmsg : {id: `${Date.now()}`, askmessage: usermsg, answermessage: aimsg, qid };
    const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: usermsg, placement: 'end' };
    const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: messageSending === 'interrupt' ? aimsg : _rtmsg.answermessage, placement: 'start' };
    const _messages = _.cloneDeep(messages);
    _messages.push(_usermsg);
    _messages.push(_replymsg);
    setMessages(_messages);
    localStorage.setItem(`bublelist${userInfo.userId}${qtype}${qid}`, JSON.stringify(_messages));
    setMessageSending('stop');
  }

  const showError = (error: any) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 403) {
        setIsModalLogin(true);
        setMessageSending('stop');
        setUserInfo({});
      } else {
        message.error(data.desc || `服务器错误: ${status}`);
      }
    } else {
      message.error("网络异常，请检查连接");
    }
  }

  const submitAsk = (askmessage: string, prefixmsg?: string) => {
    const postAsk = isWWW ? axios.post(`/api/aliaiagent/questionchat/${qtype}/${qid}`, { askmessage }) : axios.post(`${apiHost}/api/proxy/aliaiagent/questionchat/${qtype}/${qid}`, { askmessage }, { withCredentials: true });
    postAsk.then((res) => {
      if (res.status === 200 && res.data.success) {
        const msg2in1 = res.data.data.messagelog;
        if (prefixmsg) {
          setAimsg(`${prefixmsg}\n${msg2in1.answermessage}`);
          msg2in1.answermessage = `${prefixmsg}\n${msg2in1.answermessage}`;
        } else {
          setAimsg(msg2in1.answermessage);
        }
        setRtmsg(msg2in1);
        setMessageSending('sending');
        setDuration(Date.now());
      } else {
        setMessageSending('stop');
        if (res.data.count === -1) {
          setIsModalService(true);
        } else {
          message.error(res.data.desc || res.statusText)
        }
      }
    }).catch((error) => {
      showError(error);
      setMessageSending('stop');
    });
  }

  const submitAskItem = (key:number, label:string) => {
      if (messageSending === 'interrupt') {
        const articles = document.getElementsByClassName('ant-bubble-start');
        const last: any = articles[articles.length - 1];
        if (last.style.filter === 'blur(10px)') {
          last.style.filter = 'blur(0)';
        } else {
          // 中止，但需要保留
          saveMsg();
        }
      }
      setMessageSending('thinking');
      setRtmsg(null);
      setUsermsg(label);
      setAimsg('思考中...');
      setDuration(-1);
      let askmessage = messages.length > 0 ? label : `${curCq.trunk}  ${label}`;
      if (key < 100) {
        askmessage = `${askmessage} 不要输出具体的代码实现`;
        submitAsk(askmessage);
      } else if (key === 100){
        const getLog = isWWW ? axios.get(`/api/mooc/getlog/${moocId}/${qid}`) : axios.get(`${apiHost}/api/mooc/getlog/${moocId}/${qid}`, { withCredentials: true });
        getLog.then((res) => {
          const code = res.data.success ? res.data.data.code : '';
          if (code) {
            askmessage = `${askmessage} 我的代码:${code} 结合题目要求，为我分析一下我的代码实现是否正确，如果不正确，请指出其中错误。`;
            submitAsk(askmessage);
          } else {
            const _rtmsg = {id: `${Date.now()}`, askmessage: label, answermessage: '这道题你还没有做答，我无法为你的代码展开分析.', qid };
            const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: label, placement: 'end' };
            const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: _rtmsg.answermessage, placement: 'start' };
            const _messages = _.cloneDeep(messages);
            _messages.push(_usermsg);
            _messages.push(_replymsg);
            setMessages(_messages);
            localStorage.setItem(`bublelist${userInfo.userId}${qtype}${qid}`, JSON.stringify(_messages));
            setMessageSending('stop');
          }
        })
      } else if (key === 101){
        const _rtmsg = {id: `${Date.now()}`, askmessage: label, answermessage: qtype === 'question' ? `\`\`\` ${curCq.content || '未设置标准答案'} \`\`\`` : curCq.ansJsonStr, qid };
        const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: label, placement: 'end' };
        const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: _rtmsg.answermessage, placement: 'start' };
        const _messages = _.cloneDeep(messages);
        _messages.push(_usermsg);
        _messages.push(_replymsg);
        setMessages(_messages);
        localStorage.setItem(`bublelist${userInfo.userId}${qtype}${qid}`, JSON.stringify(_messages));
        setMessageSending('stop');
      } else if (key === 102){
        askmessage = `${askmessage} 官方的答案代码:${curCq.content} 结合题目要求，为我详细解读下答案代码。`;
        submitAsk(askmessage);
      } else if (key === 103){
        if (curCq.analysis) {
          const _rtmsg = {id: `${Date.now()}`, askmessage: label, answermessage: curCq.analysis, qid };
          const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: label, placement: 'end' };
          const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: _rtmsg.answermessage, placement: 'start' };
          const _messages = _.cloneDeep(messages);
          _messages.push(_usermsg);
          _messages.push(_replymsg);
          setMessages(_messages);
          localStorage.setItem(`bublelist${userInfo.userId}${qtype}${qid}`, JSON.stringify(_messages));
          setMessageSending('stop');
        } else {
          askmessage = qtype === 'question' ? `${askmessage} 官方的答案代码:${curCq.content} 结合题目要求，为我输出解题解析。` : `${askmessage} 结合题目要求，为我输出解题解析。`;
          submitAsk(askmessage, '没有找到官方解析，但我可以给你做详细解析，希望对你有帮助.');
        }
      } else {
        message.error(key);
      }
  }

  const handleSubmit = () => {
    if (!userInfo.userId) {
      setIsModalLogin(true);
      return;
    }
    if (!inputValue.trim()) return;
    if (messageSending === 'interrupt') {
      const articles = document.getElementsByClassName('ant-bubble-start');
      const last: any = articles[articles.length - 1];
      if (last.style.filter === 'blur(10px)') {
        last.style.filter = 'blur(0)';
      } else {
        // 中止，但需要保留
        saveMsg();
      }
    }
    setMessageSending('thinking');
    setRtmsg(null);
    const _v = inputValue;
    setUsermsg(_v);
    setAimsg('思考中...');
    setDuration(-1);
    // setLines([]);
    setInputValue('');
    // submitAndGcStream(_v);
    const askmessage = messages.length > 0 ? _v : `${curCq.trunk}  ${_v}`;
    submitAsk(askmessage);
  };

  // 处理取消发送
  const handleCancel = () => {
    // abortXRequest();
    if (duration === -1) {
      // const _rtmsg = _.cloneDeep(rtmsg);
      // _rtmsg.answermessage = '已暂停生成。';
      setMessageSending('interrupt');
      setAimsg('已暂停生成。')
    } else {
      const ltime = Date.now() - duration;
      const chars = ltime / 100;
      if (chars < rtmsg.answermessage.length) {
        // const _rtmsg = _.cloneDeep(rtmsg);
        // _rtmsg.answermessage = rtmsg.answermessage.substring(0, chars + 1);
        setMessageSending('interrupt');
        setAimsg(rtmsg.answermessage.substring(0, chars + 1));
      }
    }
  };

  const selectAsk = (selAskId: string) => {
    setSelAskId(selAskId);
    submitAskItem(Number(selAskId), _.find(getAskItems(qtype || ''), { key: selAskId })?.label || '');
  }

  const reAsk = () => {
    const articles = document.getElementsByClassName('ant-bubble-start');
    const last: any = articles[articles.length - 1];
    last.style.filter = 'blur(10px)';
    setInputValue(usermsg);
    senderRef.current.focus();
  }

  const toLogin = () => {
    if (isWWW) {
      window.location.href = `/apps/login?fromUrl=${encodeURIComponent(document.location.pathname)}`
    } else {
      window.location.href = `/login?fromUrl=${encodeURIComponent(document.location.pathname)}`
    }
    
  }

  const toLogout = () => {
    if (isWWW) {
      axios.get('/app/system/logout');
    } else {
      axios.get(`${apiHost}/api/system/logout`, { withCredentials: true }).then(() => {
            window.location.href = '/login';
          });
    }
    setUserInfo({});
  }

  const checkWeiXinNav = () => {
    const wechatInfo = window.navigator.userAgent.match(/MicroMessenger\/([\d\\.]+)/i);
    if (wechatInfo && wechatInfo[1] >= '5.0') {
      return true;
    }
    return false;
  }

  const towxrecharge = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const attach = queryParams.get('state');
    if (checkWeiXinNav() && code && attach) {
      // 进微信支付
      wxrecharge();
    }
  }

  const wxrecharge = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const attach = decodeURIComponent(queryParams.get('state') || '');
    const paramsObject = JSON.parse(attach);
    const { userId, price, tokens } = paramsObject;
      // const bizUrlNext = window.location.pathname.substring(1);
      // const paramsObject = this.paramsToObject(attach);
      const getOpenid = isWWW ? axios.get(`/api/weixinpay/openid/${code}`) : axios.get(`${apiHost}/api/weixinpay/openid/${code}`, { withCredentials: true });
      getOpenid.then((res) => {
        if (res.data.success) {
          const opData = res.data.data;
          const params = {
              tokens,
              userId,
              openid: opData.openid,
              weixinName: opData.nickname,
              price,
              attach,
            };
          const rechargeAlitotens = isWWW ? axios.post('/api/weixinpay/rechargealitokens', params) : axios.post(`${apiHost}/api/weixinpay/rechargealitokens`, params, { withCredentials: true });
          rechargeAlitotens.then((resp) => {
            const {
              data, success, count
            } = resp.data;
            if (success) {
              if (count === 1) {
                setWxPayParams(
                  {
                    prepay_id: data.prepay_id,
                    nonce_str: data.nonce_str,
                    appid: data.appid,
                  });
              }
            } else {
              message.error('操作失败，稍后再试!');
            }
          });
        }
      });
    }

  const wxpay = () => {
    const WeixinJSBridge = window.WeixinJSBridge;
    if (typeof WeixinJSBridge === 'undefined') {
      if (window.document.addEventListener) {
        window.document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
      }
      //  else if (window.document.attachEvent) {
      //   window.document.attachEvent('WeixinJSBridgeReady', onBridgeReady);
      //   window.document.attachEvent('onWeixinJSBridgeReady', onBridgeReady);
      // }
    } else {
      onBridgeReady();
    }
  }

  const randomString = (len: number) => {
    const leng = len || 32;
    const _chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678'; /** **默认去掉了容易混淆的字符oOLl,9gq,Vv,Uu,I1*** */
    const maxPos = _chars.length;
    let pwd = '';
    for (let i = 0; i < leng; i += 1) {
      pwd += _chars.charAt(Math.floor(Math.random() * maxPos));
    }
    return pwd;
  }

  const generateSign = (appId: string, nonceStr: string, signType: string, pack: string, timeStamp: number) => {
      let Str = `appId=${appId}&nonceStr=${nonceStr}&package=${pack}&signType=${signType}&timeStamp=${timeStamp}`;
      Str += '&key=itbeginwaen2018db56753388jgfv211';
      const sign = md5(Str).toUpperCase();
      return sign;
    }

  const onBridgeReady = () => {
    const { appid, prepay_id } = wxPayParams;
    // const queryParams = new URLSearchParams(window.location.search);
    // const attach = decodeURIComponent(queryParams.get('state') || '');
    // const paramsObject = paramsToObject(attach);
    // const { userId, price, tokens } = paramsObject;
    const bizUrlNext = window.location.pathname;
    const timeStamp10: number = Number(new Date().getTime().toString().substring(0, 10));
    const packageWx = `prepay_id=${prepay_id}`;
    const nonce_str = randomString(31);
    const sign = generateSign(appid, nonce_str, 'MD5', packageWx, timeStamp10);
    window.WeixinJSBridge?.invoke(
      'getBrandWCPayRequest',
      {
        appId: appid, // 公众号名称，由商户传入
        timeStamp: `${timeStamp10}`, // 时间戳，自1970年以来的秒数
        nonceStr: nonce_str, // 随机串
        package: packageWx,
        signType: 'MD5', // 微信签名方式：
        paySign: sign, // 微信签名
      },
      (res: any) => {
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          window.location.href = bizUrlNext;
        } // 使用以上方式判断前端返回,微信团队郑重提示：res.err_msg将在用户支付成功后返回    ok，但并不保证它绝对可靠。
      },
    );
  }

  const toBuy = (r:any) => {
    if (checkWeiXinNav()) {
       // 在微信里，默认微信支付
       const _data = {
        userId: userInfo.userId,
        tokens: r.tokens,
        price: r.price
      };
      const state = encodeURIComponent(JSON.stringify(_data));
      let url = window.location.href;
      const encodeUri = encodeURIComponent(url);
      window.location.href = 'https://open.weixin.qq.com/connect/oauth2/authorize?appid='
      + `wx973c5b220058cbed&redirect_uri=${encodeUri}`
      + `&response_type=code&scope=snsapi_userinfo&state=${state}`
      + '#wechat_redirect';
    } else {
      // 其他情况默认到支付宝，支付宝又分手机客户端还是pc网页上支付
      try {
        if (isWWW) {
          const bizUrlNext = window.location.pathname.substring(1);
          axios.post(`/api/pay/${isMobile().any ? 'wap' : 'page'}/buyalitokens/${userInfo.userId}/${r.tokens}`, null, { params: { price: r.price, nextBizUrl: bizUrlNext } }).then((res1) => {
            if (res1.data.success && res1.data.count === 1) {
              document.write(res1.data.data);
            }
          });
        } else {
          const bizUrlNext = window.location.href;
          axios.post(`${apiHost}/api/pay/${isMobile().any ? 'wap' : 'page'}/buyalitokens/${userInfo.userId}/${r.tokens}`, null, { params: { price: r.price, nextBizUrl: bizUrlNext }, withCredentials: true }).then((res1) => {
            if (res1.data.success && res1.data.count === 1) {
              document.write(res1.data.data);
            }
          });
        }
      } catch (error) {
        message.error('操作失败，稍后再试!');
      }
    }
  }

  return (
    <XProvider direction="ltr">
      <Flex style={{ height: '100vh', width: '100vw', boxSizing: 'border-box', padding: '5px' }} gap={12}>
        <Sidebar 
          showLeftBar={showLeftBar}
          setShowLeftBar={setShowLeftBar}
          qtype={qtype}
          selAskId={selAskId}
          userInfo={userInfo}
          toLogin={toLogin}
          toLogout={toLogout}
          selectAsk={selectAsk}
        />
        
        <Flex vertical style={{ flex: 1, position: 'relative' }} gap={8}>
          <ChatHeader 
            showLeftBar={showLeftBar}
            setShowLeftBar={setShowLeftBar}
            qid={qid}
            qname={curCq.name || curCq.trunk}
          />
          
          <MessageList
            messages={messages}
            usermsg={usermsg}
            aimsg={aimsg}
            messageSending={messageSending}
            shouldScroll={shouldScroll}
            completeTyping={completeTyping}
            reAsk={reAsk}
            ref={scrollRef}
          />
          {/* <PromptSuggestions /> */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            padding: '16px 10px',
            zIndex: 100,
          }}
            id="sendmessagediv"
          >
            <Sender 
              value={inputValue}
              loading={messageSending === 'thinking' || messageSending === 'sending'}
              onChange={(v) => setInputValue(v)}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              ref={senderRef}
            />
          </div>
        </Flex>
        
        {/* 思维链组件（可选） */}
        {false && (
          <>
            <Divider type="vertical" style={{ height: '100%' }} />
            <ThoughtChain
              style={{ width: 200 }}
              items={[
                { title: '思考中...', status: 'pending', icon: <LoadingOutlined /> }
              ]}
            />
          </>
        )}
      </Flex>
      <Modal
        title="登录提示"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalLogin}
        onOk={toLogin}
        onCancel={() => setIsModalLogin(false) }
        okText="确定"
        cancelText="取消"
      >
        <p>先登录，马上回来?</p>
      </Modal>
      <Modal
        title="服务购买"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalService}
        onCancel={() => setIsModalService(false) }
        footer={null}
      >
        <p>您当前的tokens为0，会影响你的使用，请及时购买tokens服务</p>
        <Table
          pagination={false}
          dataSource={[
            {
              tokens: '10000',
              period: '一年',
              price: '60',
            },
            {
              tokens: '60000',
              period: '一年',
              price: '350',
            },
            {
              tokens: '600000',
              period: '一年',
              price: '3000',
            },
          ]}
          columns={[
            {
              title: 'tokens(单位千tokens)',
              dataIndex: 'tokens',
              key: 'tokens',
            },
            {
              title: '有效期',
              dataIndex: 'period',
              key: 'period',
            },
            {
              title: '价格',
              dataIndex: 'price',
              key: 'price',
              render: (text) => <><span>¥</span>{text}</>,
            },
            {
              title: '',
              dataIndex: 'action',
              key: 'action',
              render: (text, record) => <Button onClick={() => { toBuy(record) }}>购买</Button>,
            },
          ]} 
        />
      </Modal>
    </XProvider>
  );
};

export default AiQuestionChatApp;